import { useCallback } from 'react'
import { useShallow } from 'zustand/react/shallow'

import { useDocumentPageQuery } from '@/pages/DocumentPage/queries'
import { useCADQuery } from '@/services/queries/cads'
import { ExplosionsState, useCADPageStore } from '@/state/cad'
import { useReorderSteps } from './useReorderSteps'

import type { Step } from '@/services/queries/operation_steps/types'
import { useHighlightStep } from '../../../ControlPanel/hooks/useHighlightStep'

export const useToggleStepCardsList = () => {
  const { data: docData } = useDocumentPageQuery()
  const { data: cadData } = useCADQuery()
  const { mutateAsync: reorderSteps } = useReorderSteps()
  const getCadPageState = useCADPageStore(useShallow((state) => state.getState))
  const setCadPageState = useCADPageStore(useShallow((state) => state.setState))
  const colorMap = useCADPageStore(useShallow((state) => state.colorMap))
  const parentDocumentPageId = useCADPageStore(
    (state) => state.subAssemblyDocumentPageId || null,
  )

  const assemblyTree = cadData.rawAssemblyTree
  const gltf = cadData.gltf
  const documentType = docData?.documentType

  const highlightStep = useHighlightStep(gltf)
  const resetHighlightedParts = useCADPageStore(
    (state) => state.resetHighlightedParts,
  )

  return useCallback(
    async ({
      steps,
      previousSteps,
      documentVersionId,
      isReadOnly,
    }: {
      steps: Step[]
      previousSteps: Step[]
      documentVersionId: string
      isReadOnly: boolean
    }) => {
      const reorderedSteps = steps.map((step, i) => ({
        ...step,
        order_number: i + 1,
      })) as Step[]

      if (!assemblyTree || !gltf) return

      const cadPageState = getCadPageState()
      const didStepsChange = previousSteps.some(
        (step, i) => step.id !== reorderedSteps[i]?.id,
      )

      const operationStep = cadPageState.operationStep
      const isStepCardActive = Boolean(operationStep?.isActive)
      const hiddenParts = cadPageState.hiddenParts
      const explosions = cadPageState.explosions
      const highlighting = cadPageState.highlighting
      const highlightedParts = cadPageState.highlightedParts
      const isExplosionLinesEnabled = cadPageState.isExplosionLinesEnabled

      const activeStep = reorderedSteps?.find(
        (step) => step.id === operationStep?.stepId,
      )

      const shouldSetVisibility = documentType !== 'project_tracker'
      const shouldToggleOff = isStepCardActive && !didStepsChange

      if (shouldSetVisibility) {
        gltf.setVisibilityForOperationSteps({
          assemblyTree,
          steps: reorderedSteps,
          activeStep: shouldToggleOff ? null : activeStep,
          hiddenParts,
          explosions,
          isExplosionLinesEnabled,
        })
      }

      if (didStepsChange && !isReadOnly) {
        await reorderSteps({
          documentVersionId,
          steps: reorderedSteps,
          parentDocumentPageId,
        }).catch((error) => {
          console.error('Failed to reorder steps:', error)
        })
      }

      if (didStepsChange) {
        gltf.unhighlightParts(colorMap)
        setCadPageState({
          explosionsState: ExplosionsState.DISABLED,
          selectedParts: [],
        })
      }

      setTimeout(() => {
        if (highlightedParts.length) resetHighlightedParts()

        if (shouldToggleOff) {
          setCadPageState({
            operationStep: null,
          })
        } else {
          const updatedStep = operationStep
            ? { ...operationStep, isActive: true }
            : null
          setCadPageState({ operationStep: updatedStep })
          if (highlighting && activeStep) highlightStep(activeStep)
        }
      }, 50)
    },
    [
      assemblyTree,
      colorMap,
      documentType,
      getCadPageState,
      gltf,
      parentDocumentPageId,
      reorderSteps,
      setCadPageState,
      highlightStep,
      resetHighlightedParts,
    ],
  )
}
